import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { has, isArray } from "lodash";

import api from "../../services/api";
import socketWorker from "../../services/SocketWorker";
import toastError from "../../errors/toastError";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(null);
  const [isHorizontal, setIsHorizontal] = useState(true);

  // Requisições: adiciona token, ou bloqueia se logout completo
  api.interceptors.request.use(config => {
    if (localStorage.getItem("logout-completed") === "true") {
      return Promise.reject(new Error("Usuário deslogado"));
    }
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
      setIsAuth(true);
    }
    return config;
  }, error => Promise.reject(error));

  // Respostas: trata sessão expirada, 403 e 401
  api.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config;
      const token = localStorage.getItem("token");

      if (!token) return Promise.reject(error);

      if (error?.response?.data?.message === "ERR_SESSION_EXPIRED") {
        try {
          await api.delete("/auth/logout");
        } catch (_) {}
        handleLocalLogout();
        return Promise.reject(error);
      }

      if (error?.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const { data } = await api.post("/auth/refresh_token");
          if (data?.token) {
            localStorage.setItem("token", data.token);
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
            return api(originalRequest);
          }
        } catch (err) {
          handleLocalLogout();
          return Promise.reject(err);
        }
      }

      if (error?.response?.status === 401) {
        handleLocalLogout();
      }

      return Promise.reject(error);
    }
  );

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/refresh_token");
        if (data?.token) {
          localStorage.setItem("token", data.token);
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          setUser(data.user);
          setIsAuth(true);

          if (
            !localStorage.getItem("public-token") ||
            localStorage.getItem("public-token") !== data.publicToken
          ) {
            localStorage.setItem("public-token", data.publicToken);
          }
        }
      } catch (err) {
        handleLocalLogout();
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (user?.id && user?.companyId) {
      let io = socket;
      if (!io) {
        io = socketWorker(user.companyId, user.id);
        setSocket(io);
        try {
          // Expor referência global controlada para reutilização (SocketContext adapter)
          window.__APP_SOCKET = io;
        } catch (_) {}
      } else if (typeof window !== 'undefined' && window.__APP_SOCKET !== io) {
        window.__APP_SOCKET = io;
      }

      io.on(`company-${user.companyId}-user`, data => {
        if (data.action === "update" && data.user.id === user.id) {
          setUser(data.user);
        }
      });

      return () => io.off(`company-${user.companyId}-user`);
    }
  }, [user, socket]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (socket) socket.disconnect();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [socket]);

  const handleLogin = async userData => {
    localStorage.removeItem("logout-completed");
    // Não acionar loading global durante o submit do login para evitar piscar na tela de Login

    try {
      const { data } = await api.post("/auth/login", userData);
      const { company } = data.user;

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(s => s.key === "campaignsEnabled");
        if (setting?.value === "true") localStorage.setItem("cshow", null);
      }

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(s => s.key === "sendSignMessage");
        if (setting?.value === "enabled") localStorage.setItem("sendSignMessage", true);
      }

      localStorage.setItem("profileImage", data.user.profileImage);
      moment.locale("pt-br");

      const dueDate = data.user.company.id === 1
        ? "2999-12-31T00:00:00.000Z"
        : data.user.company.dueDate;

      const vencimento = moment(dueDate).format("DD/MM/yyyy");
      const dias = moment(dueDate).diff(moment(), "days");

      localStorage.setItem("token", data.token);
      localStorage.setItem("public-token", data.publicToken);
      localStorage.setItem("companyDueDate", vencimento);

      api.defaults.headers.Authorization = `Bearer ${data.token}`;
      setUser(data.user);
      setIsAuth(true);

  // Removido: toast de "login efetuado com sucesso" por solicitação
      if (dias < 5) toast.warn(`Sua assinatura vence em ${dias} ${dias === 1 ? "dia" : "dias"}`);
      history.push("/tickets");
    } catch (err) {
      // Não exibir toast na tela de login; propagar erro estruturado para tratamento inline
      const status = err?.response?.status;
      const code = err?.response?.data?.error || err?.response?.data?.message;
      const message = err?.response?.data?.message || err?.message || "Erro ao efetuar login";

      // Retornar um objeto simples para o componente de Login lidar
      const structured = { status, code, message };
      throw structured;
    } finally {
      // Não alterar o loading global aqui; a tela de Login já controla estado de submissão local
    }
  };

  const handleLogout = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        cleanupLogout();
        return;
      }
      localStorage.setItem("logout-completed", "true");
      api.defaults.headers.Authorization = undefined;
      await api.delete("/auth/logout");
    } catch (_) {}
    finally {
      cleanupLogout();
    }
  };

  const cleanupLogout = () => {
    setIsAuth(false);
    setUser({});
    setLoading(false);

    // Dismiss de quaisquer toasts pendentes na tela
    try { toast.dismiss(); } catch (_) {}

    localStorage.removeItem("token");
    localStorage.removeItem("public-token");
    localStorage.removeItem("cshow");
    localStorage.setItem("logout-completed", "true");

    if (socket) {
      socket.disconnect();
      setSocket(null);
    }
    try { if (window.__APP_SOCKET) delete window.__APP_SOCKET; } catch (_) {}

    setTimeout(() => {
      window.location.href = "/login";
    }, 300);
  };

  const handleLocalLogout = () => {
    try {
      api.delete("/auth/logout").catch(() => {});
    } finally {
      cleanupLogout();
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (_) {
      return null;
    }
  };

  useEffect(() => {
    // Remover flag de logout ao inicializar
    localStorage.removeItem("logout-completed");
    
    return () => {
      // NÃO definir logout-completed aqui!
      // Isso estava causando o bloqueio de todas as requisições durante navegação
      console.log("useAuth cleanup - componente desmontado");
    };
  }, []);

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
    isHorizontal,
    setIsHorizontal
  };
};

export default useAuth;
